import type React from 'react'
import type { CTAProps, OrbitItem } from './types'
import { CtaChecklist } from './cta-checklist'
import { CtaSplitMedia } from './cta-split-media'
import { CtaCentered } from './cta-centered'
import { CtaCenteredCheck } from './cta-centered-check'
import { CtaOrbiting } from './cta-orbiting'
import { CtaOrbitingInstall } from './cta-orbiting-install'

export type { CTAProps }

function buildButtons(props: { callToAction?: string; callToActionUrl?: string; secondaryCallToAction?: string; secondaryCallToActionUrl?: string }) {
  return {
    primary: props.callToAction ? { text: props.callToAction, url: props.callToActionUrl ?? '' } : undefined,
    secondary: props.secondaryCallToAction ? { text: props.secondaryCallToAction, url: props.secondaryCallToActionUrl ?? '' } : undefined,
  }
}

function buildOrbitArrays(items?: (OrbitItem & { orbit: 'inner' | 'middle' | 'outer' })[]) {
  if (!items?.length) return {}
  const inner = items.filter((i) => i.orbit === 'inner').map(({ type, src, fallback, alt }) => ({ type, src, fallback, alt }))
  const middle = items.filter((i) => i.orbit === 'middle').map(({ type, src, fallback, alt }) => ({ type, src, fallback, alt }))
  const outer = items.filter((i) => i.orbit === 'outer').map(({ type, src, fallback, alt }) => ({ type, src, fallback, alt }))
  return {
    ...(inner.length ? { innerOrbit: inner } : {}),
    ...(middle.length ? { middleOrbit: middle } : {}),
    ...(outer.length ? { outerOrbit: outer } : {}),
  }
}

/**
 * CTA variant switch component
 *
 * Routes to the appropriate CTA component based on variant.
 * Props are a discriminated union — each variant only accepts its own props.
 */
export function CTA(props: CTAProps): React.ReactElement {
  switch (props.variant) {
    case 'centered':
      return (
        <CtaCentered
          heading={props.title}
          description={props.description}
          items={props.items}
          buttons={buildButtons(props)}
          background={props.background}
          className={props.className}
        />
      )

    case 'centered-check':
      if (props.ctaMode === 'install-snippet') {
        return (
          <CtaCenteredCheck
            ctaMode="install-snippet"
            heading={props.title}
            description={props.description}
            items={props.items}
            package={props.package}
            command={props.command}
            background={props.background}
            className={props.className}
          />
        )
      }
      return (
        <CtaCenteredCheck
          heading={props.title}
          description={props.description}
          items={props.items}
          primaryButton={props.callToAction ? { text: props.callToAction, url: props.callToActionUrl ?? '' } : undefined}
          secondaryButton={props.secondaryCallToAction ? { text: props.secondaryCallToAction, url: props.secondaryCallToActionUrl ?? '' } : undefined}
          background={props.background}
          className={props.className}
        />
      )

    case 'split-media':
      return (
        <CtaSplitMedia
          gradient={false}
          heading={props.title}
          description={props.description}
          buttons={buildButtons(props)}
          image={props.image}
          mediaType={props.mediaType}
          codeWindow={props.codeWindow}
          codeTheme={props.codeTheme}
          className={props.className}
        />
      )

    case 'split-media-gradient':
      return (
        <CtaSplitMedia
          label={props.badge}
          heading={props.title}
          description={props.description}
          buttons={buildButtons(props)}
          image={props.image}
          mediaType={props.mediaType}
          codeWindow={props.codeWindow}
          codeTheme={props.codeTheme}
          className={props.className}
        />
      )

    case 'checklist':
      return (
        <CtaChecklist
          heading={props.title}
          description={props.description}
          button={props.callToAction ? { text: props.callToAction, url: props.callToActionUrl ?? '' } : undefined}
          items={props.items}
          className={props.className}
        />
      )

    case 'orbiting': {
      const orbits = buildOrbitArrays(props.orbitingItems)
      if (props.ctaMode === 'install-snippet') {
        return (
          <CtaOrbitingInstall
            heading={props.title}
            description={props.description}
            package={props.package}
            command={props.command}
            {...orbits}
            className={props.className}
          />
        )
      }
      return (
        <CtaOrbiting
          heading={props.title}
          description={props.description}
          button={props.callToAction ? { text: props.callToAction, url: props.callToActionUrl ?? '' } : undefined}
          {...orbits}
          className={props.className}
        />
      )
    }

    default: {
      void (props satisfies never)
      return (
        <CtaCentered
          heading={(props as any).title}
          description={(props as any).description}
          className={(props as any).className}
        />
      )
    }
  }
}

/**
 * CTA Section - alias for CTA component
 * Matches the mdxui naming convention
 */
export const CTASection = CTA

export default CTA
